export const PUBLIC_REGISTRATION_HEIGHT_MESSAGE_TYPE = "eventflow:registration-height";
export const PUBLIC_REGISTRATION_HEIGHT_THROTTLE_MS = 150;

export interface PublicRegistrationHeightMessage {
    type: typeof PUBLIC_REGISTRATION_HEIGHT_MESSAGE_TYPE;
    height: number;
}

interface HeightReporterOptions {
    embedded: boolean;
    element: HTMLElement | null;
    target?: Pick<Window, "postMessage"> | null;
    self?: Window;
    throttleMs?: number;
}

const measureHeight = (element: HTMLElement): number =>
    Math.ceil(Math.max(element.getBoundingClientRect().height, element.scrollHeight));

/**
 * Posts the embedded registration page height to the host page so its iframe can resize.
 * The message only carries a pixel height; never add form values or participant data to it.
 * Frame ancestors are already restricted by CSP, and the host snippet checks event.origin.
 */
export const startPublicRegistrationHeightReporter = ({
    embedded,
    element,
    target = typeof window !== "undefined" ? window.parent : null,
    self = typeof window !== "undefined" ? window : undefined,
    throttleMs = PUBLIC_REGISTRATION_HEIGHT_THROTTLE_MS
}: HeightReporterOptions): (() => void) => {
    if (!embedded || !element || !target || target === self || typeof ResizeObserver === "undefined") {
        return () => undefined;
    }

    let lastPostedHeight: number | undefined;
    let lastPostedAt = Number.NEGATIVE_INFINITY;
    let pendingTimer: ReturnType<typeof setTimeout> | undefined;

    const post = () => {
        pendingTimer = undefined;
        lastPostedAt = Date.now();
        const height = measureHeight(element);
        if (height <= 0 || height === lastPostedHeight) {
            return;
        }

        lastPostedHeight = height;
        const message: PublicRegistrationHeightMessage = {type: PUBLIC_REGISTRATION_HEIGHT_MESSAGE_TYPE, height};
        target.postMessage(message, "*");
    };

    const schedule = () => {
        if (pendingTimer !== undefined) {
            return;
        }

        const wait = lastPostedAt + throttleMs - Date.now();
        if (wait <= 0) {
            post();
            return;
        }

        pendingTimer = setTimeout(post, wait);
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    schedule();

    return () => {
        observer.disconnect();
        if (pendingTimer !== undefined) {
            clearTimeout(pendingTimer);
            pendingTimer = undefined;
        }
    };
};
